<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild :settings="fields.settings" :container-page="fields.containerPage" />
        </div>

        <div class="mb-4">
          <div class="d-flex align-center mb-2" style="gap: 10px">
            <label class="label-input mb-0">Card Title</label>
            <input type="checkbox" v-model="fields.cardTitle.show" />
          </div>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.cardTitle.content" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Media Image</label>
          <div class="group-item">
            <UploadImage
              @callback="handleUploadMediaImage"
              :image="{ imageSrc: fields.media.src || '' }"
              :height="`200`"
              :typeUpload="'image'"
              @callbackDelete="handleRemoveMediaImage"
            />
          </div>
        </div>

        <div v-for="card in cardConfigs" :key="card.key" class="mb-4">
          <label class="label-input">{{ card.label }}</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="6">
                <ColorPicker
                  v-model="fields[card.key].colorBg"
                  :name="`${card.key}ColorBg`"
                  label="Background Color"
                  default-color="#ffffff"
                />
              </v-col>
              <v-col cols="12" md="6">
                <ColorPicker
                  v-model="fields[card.key].colorText"
                  :name="`${card.key}ColorText`"
                  label="Text Color"
                  default-color="#000000"
                />
              </v-col>
            </v-row>
            <v-text-field v-model="fields[card.key].title" label="Card Title" hide-details class="mt-2" />

            <div class="d-flex justify-space-between align-center mt-5 mb-2">
              <label class="field-label mb-0">Items</label>
              <v-btn small text color="primary" @click="handleAddItem(card.key)">
                <v-icon size="16">mdi-plus</v-icon>
                Add Item
              </v-btn>
            </div>
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in fields[card.key].items" :key="`${card.key}-item-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <span>Item {{ index + 1 }}</span>
                    <v-btn icon @click.stop="handleRemoveItem(card.key, index)">
                      <v-icon size="18" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <label class="field-label">Main Icon</label>
                  <v-radio-group v-model="item.useSvgMain" row>
                    <v-radio label="Icon Image" :value="false"></v-radio>
                    <v-radio label="Icon SVG" :value="true"></v-radio>
                  </v-radio-group>
                  <UploadImage
                    v-if="!item.useSvgMain"
                    @callback="obj => handleUploadIconMainImage(card.key, index, obj)"
                    :image="{ imageSrc: item.iconMain || '' }"
                    :height="`100`"
                    :typeUpload="'image'"
                    @callbackDelete="() => handleRemoveIconMainImage(card.key, index)"
                  />
                  <UploadSVG
                    v-else
                    :svg="{ svgContent: item.iconMainSvg }"
                    :height="`100`"
                    @callback="obj => handleUploadIconMainSvg(card.key, index, obj)"
                    @callbackDelete="() => handleRemoveIconMainSvg(card.key, index)"
                  />

                  <div class="d-flex align-center mt-5 mb-2" style="gap: 10px">
                    <label class="field-label mb-0">Sub Icon</label>
                    <input type="checkbox" v-model="item.iconSub.show" />
                  </div>
                  <UploadSVG
                    :svg="{ svgContent: item.iconSub.svg }"
                    :height="`80`"
                    @callback="obj => handleUploadIconSubSvg(card.key, index, obj)"
                    @callbackDelete="() => handleRemoveIconSubSvg(card.key, index)"
                  />

                  <v-text-field v-model="item.text" label="Text" hide-details class="mt-5" />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <div class="mb-4">
          <ButtonPrimary :button="fields.button" />
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer />
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import UploadSVG from '@/components/form/uploadFile/UploadSVG.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update.js';
import ButtonPrimary from './common/button-primary.vue';
import ColorPicker from './common/color-picker.vue';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

const DEFAULT_ITEM = {
  useSvgMain: false,
  iconMain: '',
  iconMainSvg: '',
  iconSub: { show: true, svg: '' },
  text: 'New item',
};

const DEFAULT_BUTTON = {
  show: true,
  text: 'ADD TO CART',
  link: '#',
  backgroundColor: '#ccf46f',
  color: '#23372b',
  radius: 100,
  iconPrefix: { show: false, src: '' },
  iconSuffix: { show: true, src: '' },
  subBtn: { show: false, text: '' },
};

const clone = value => JSON.parse(JSON.stringify(value));

export default {
  name: 'SofyreComparisonSectionEditor',
  components: { ButtonPrimary, ColorPicker, SectionSettingsContainerFuild, UploadImage, UploadSVG },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      cardConfigs: [
        { key: 'cardLeft', label: 'Card Left' },
        { key: 'cardRight', label: 'Card Right' },
      ],
      fields: {
        title: 'Sofyre Comparison Section',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#FFFFFF',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: { enableStyle: false, background: '#FFFFFF' },
        cardTitle: {
          show: true,
          content:
            '<h2>Safe Home. Clean Conscience.</h2><p>Safe For Your Home Powerful enough to send mice running permanently. Gentle enough to place anywhere your family lives.</p>',
        },
        cardLeft: { colorBg: '#FFFFFF', colorText: '#000000', title: "We're Proudly Free From:", items: [] },
        cardRight: { colorBg: '#23372B', colorText: '#FFFFFF', title: 'And proudly:', items: [] },
        media: { src: '' },
        button: clone(DEFAULT_BUTTON),
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(value) {
        if (value && value.sectionExtract) this.applyExtract(value.sectionExtract);
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      const extractedButton = extract.button || {};
      const extractedCardLeft = extract.cardLeft || {};
      const extractedCardRight = extract.cardRight || {};
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        cardTitle: { ...this.fields.cardTitle, ...(extract.cardTitle || {}) },
        media: { ...this.fields.media, ...(extract.media || {}) },
        cardLeft: {
          ...this.fields.cardLeft,
          ...extractedCardLeft,
          items: Array.isArray(extractedCardLeft.items) ? clone(extractedCardLeft.items) : [],
        },
        cardRight: {
          ...this.fields.cardRight,
          ...extractedCardRight,
          items: Array.isArray(extractedCardRight.items) ? clone(extractedCardRight.items) : [],
        },
        button: {
          ...this.fields.button,
          ...extractedButton,
          iconPrefix: { ...this.fields.button.iconPrefix, ...(extractedButton.iconPrefix || {}) },
          iconSuffix: { ...this.fields.button.iconSuffix, ...(extractedButton.iconSuffix || {}) },
          subBtn: { ...this.fields.button.subBtn, ...(extractedButton.subBtn || {}) },
        },
      };
    },
    handleAddItem(cardKey) {
      this.fields[cardKey].items.push(clone(DEFAULT_ITEM));
    },
    handleRemoveItem(cardKey, index) {
      this.fields[cardKey].items.splice(index, 1);
    },
    handleUploadIconMainImage(cardKey, index, obj) {
      if (!this.fields[cardKey].items[index]) return;
      this.$set(this.fields[cardKey].items[index], 'iconMain', (obj && obj.imageSrc) || '');
    },
    handleRemoveIconMainImage(cardKey, index) {
      if (!this.fields[cardKey].items[index]) return;
      this.$set(this.fields[cardKey].items[index], 'iconMain', '');
    },
    handleUploadIconMainSvg(cardKey, index, obj) {
      if (!this.fields[cardKey].items[index]) return;
      this.$set(this.fields[cardKey].items[index], 'iconMainSvg', (obj && obj.svgContent) || '');
    },
    handleRemoveIconMainSvg(cardKey, index) {
      if (!this.fields[cardKey].items[index]) return;
      this.$set(this.fields[cardKey].items[index], 'iconMainSvg', '');
    },
    handleUploadIconSubSvg(cardKey, index, obj) {
      if (!this.fields[cardKey].items[index]) return;
      this.$set(this.fields[cardKey].items[index].iconSub, 'svg', (obj && obj.svgContent) || '');
    },
    handleRemoveIconSubSvg(cardKey, index) {
      if (!this.fields[cardKey].items[index]) return;
      this.$set(this.fields[cardKey].items[index].iconSub, 'svg', '');
    },
    handleUploadMediaImage(obj) {
      this.fields.media.src = (obj && obj.imageSrc) || '';
    },
    handleRemoveMediaImage() {
      this.fields.media.src = '';
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const html = updateHtmlFromData.sectionSofyreComparison(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html,
      });
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.label-input {
  display: block;
  margin-bottom: 10px;
  color: #000;
  font-size: 18px !important;
  font-weight: 500;
}
.field-label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
}
.group-item {
  padding: 15px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
}
.header-modal,
.footer-modal {
  position: sticky;
  left: 0;
  z-index: 999;
  padding: 0 16px;
  background: #fff;
  box-shadow: 0 0 12px #0000000f;
}
.header-modal {
  top: 0;
}
.footer-modal {
  bottom: 0;
}
</style>
